import {
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type SyntheticEvent,
} from 'react';
import type { VideoMedia, VideoReference } from '@/api/types';
import { track } from '@/analytics/tracker';
import { useT } from '@/i18n/LocaleProvider';
import { formatDuration } from '@/lib/format';
import { seekFromKey, seekFromPointer } from './seek';
import styles from './VideoDrawer.module.css';

interface Props {
  video: VideoReference;
  media: VideoMedia;
  /** Файл не играет — откат на embed площадки (PLAY-2). */
  onFallback: () => void;
}

export function posterFor(video: VideoReference): string | undefined {
  return (
    video.media?.poster_url ??
    video.extra?.keyframes?.[0] ??
    video.extra?.thumbnail_url ??
    undefined
  );
}

/**
 * Свой плеер (PLAY-2, PLAY-3): нативный <video> без библиотек, preload=none,
 * без скачивания и PiP; play/pause — слоем поверх кадра, не обработчиком на
 * <video>; полоса прогресса — ползунок перемотки (PLAY-3): клик и протяжка, с клавиатуры
 * ←/→ по секунде, с Shift — по пять; моно-таймкод. Единственный <video> в документе (PLAY-4).
 */
export function HostedPlayer({ video, media, onFallback }: Props) {
  const t = useT();
  const ref = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(true);
  const [time, setTime] = useState(0);
  const [dragging, setDragging] = useState(false);
  const started = useRef(false);
  const duration = media.duration_sec ?? video.duration_sec ?? 0;
  const seekable = duration > 0;
  const progress = seekable ? Math.min(100, (time / duration) * 100) : 0;

  // До первого play файл не загружен (preload=none): браузер запоминает позицию
  // и начинает с неё, поэтому таймкод и полоса обновляются сразу, не дожидаясь timeupdate.
  const seekTo = (next: number) => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = next;
    setTime(next);
  };

  const onSeekDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!seekable || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    seekTo(seekFromPointer(event.clientX, event.currentTarget.getBoundingClientRect(), duration));
  };

  const onSeekMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    seekTo(seekFromPointer(event.clientX, event.currentTarget.getBoundingClientRect(), duration));
  };

  const onSeekUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  };

  // Стрелки на полосе перематывают и не доходят до панели, где ←/→ листают видео.
  const onSeekKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const next = seekFromKey(event.key, event.shiftKey, ref.current?.currentTime ?? time, duration);
    if (next === null) return;
    event.preventDefault();
    event.stopPropagation();
    seekTo(next);
  };

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => {});
    else el.pause();
  };

  const onPlay = () => {
    setPaused(false);
    if (!started.current) {
      started.current = true;
      track('hosted_playback_started', {
        metadata: {
          video_id: video.id,
          platform: video.platform,
          duration_sec: duration || undefined,
        },
      });
    }
  };

  const onError = (event: SyntheticEvent<HTMLVideoElement>) => {
    track('hosted_playback_error', {
      metadata: {
        video_id: video.id,
        platform: video.platform,
        error_code: event.currentTarget.error?.code ?? null,
      },
    });
    onFallback();
  };

  return (
    <>
      <video
        ref={ref}
        className={styles.video}
        src={media.src}
        poster={posterFor(video)}
        playsInline
        preload="none"
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        aria-label={video.title}
        onPlay={onPlay}
        onPause={() => setPaused(true)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onEnded={(e) => {
          setPaused(true);
          setTime(0);
          e.currentTarget.currentTime = 0;
        }}
        onError={onError}
      />
      <button
        type="button"
        className={styles.overlay}
        onClick={toggle}
        aria-label={paused ? t('video.play') : t('video.pause')}
      >
        {paused ? <span className={styles.playGlyph} aria-hidden="true" /> : null}
      </button>
      <div
        className={styles.seek}
        role="slider"
        tabIndex={seekable ? 0 : -1}
        aria-label={t('video.seek')}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(time)}
        aria-valuetext={t('video.seekValue', {
          time: formatDuration(time),
          duration: formatDuration(duration),
        })}
        aria-disabled={seekable ? undefined : true}
        data-dragging={dragging ? 'true' : 'false'}
        data-no-swipe=""
        onPointerDown={onSeekDown}
        onPointerMove={onSeekMove}
        onPointerUp={onSeekUp}
        onPointerCancel={onSeekUp}
        onKeyDown={onSeekKey}
      >
        <div className={styles.progress}>
          <span className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className={styles.time}>
        {formatDuration(time)} / {formatDuration(duration)}
      </span>
    </>
  );
}
